/**
 * MSW Overrides: Plans & addons — Campaign discounts scenario (CVO-19136/CVO-19137)
 *
 * Forces a campaign onto the real plans and plan-addons responses, so the promo UI is
 * reachable locally without a live campaign. `price` and `subscriptionFees` come from the
 * BFF untouched; only `effectivePrice` and `discounts[]` are overwritten, derived from the
 * real `price`: one PUBLIC campaign (10% off, folded into effectivePrice) and one gated
 * campaign (5% off, not folded in). Only the first optional add-on joins the campaign.
 * The PUBLIC campaign's duration alternates FIXED/LIFETIME across discounted plan groups
 * so both promo treatments can be checked in a single run.
 *
 * Enable by setting ENABLE_MSW=true and
 * NUXT_PUBLIC_MSW_OVERRIDES_PLANS_CAMPAIGN_DISCOUNTS=true in your .env,
 * then restart the dev server.
 */

import type { HttpHandler } from 'msw';
import { http, HttpResponse, bypass } from 'msw';
import { BFF_ENDPOINTS, toMSWPattern } from '@cvo/domain';
import type { ChargeBreakdown, MultiCurrencyPrice, PlanDiscount, Price } from '@cvo/domain';

const PUBLIC_DISCOUNT_RATE = 0.10;
const GATED_DISCOUNT_RATE = 0.05;

// Raw (pre-camelization) wire shape — only the keys this override reads
type RawPrice = Omit<MultiCurrencyPrice, 'additionalCharges'> & { additional_charges?: ChargeBreakdown[] };

interface RawPlan {
  price: RawPrice;
  group?: string;
  [key: string]: unknown;
}

interface RawAddon {
  code: string;
  is_included?: boolean;
  price: RawPrice;
  [key: string]: unknown;
}

function scaleAmounts(prices: Price[], factor: number): Price[] {
  return prices.map(price => ({ ...price, amount: (parseFloat(price.amount) * factor).toFixed(2) }));
}

function scalePrice(price: RawPrice, factor: number): MultiCurrencyPrice {
  return {
    gross: scaleAmounts(price.gross, factor),
    net: scaleAmounts(price.net, factor),
    additionalCharges: (price.additional_charges ?? []).map(charge => ({
      ...charge,
      amounts: scaleAmounts(charge.amounts, factor),
    })),
  };
}

// Alternated across discounted groups so both promo treatments are reachable in one dev run:
// FIXED reverts (strikethrough + "Future billings" row), LIFETIME never does (strikethrough only).
const PUBLIC_DURATIONS: Array<PlanDiscount['duration']> = [
  { type: 'FIXED', periods: 3 },
  { type: 'LIFETIME' },
];

function toDiscounts(price: RawPrice, publicDuration: PlanDiscount['duration']): PlanDiscount[] {
  return [
    {
      // Matches the ACTIVE TIME_BOUNDED campaign served by the real campaigns endpoint.
      campaignCode: 'SUMMER-SALE-2026',
      eligibility: ['PUBLIC'],
      duration: publicDuration,
      includedInEffectivePrice: true,
      gross: scaleAmounts(price.gross, PUBLIC_DISCOUNT_RATE),
      net: scaleAmounts(price.net, PUBLIC_DISCOUNT_RATE),
    },
    {
      campaignCode: 'STUDENT-2026',
      eligibility: ['PROMO_CODE', 'EMAIL_DOMAIN'],
      duration: { type: 'FIXED', periods: 6 },
      includedInEffectivePrice: false,
      gross: scaleAmounts(price.gross, GATED_DISCOUNT_RATE),
      net: scaleAmounts(price.net, GATED_DISCOUNT_RATE),
    },
  ];
}

export function createPlansCampaignDiscountsOverrides(baseUrl: string): HttpHandler[] {
  const sanitizedBaseUrl = baseUrl.replace(/\/$/, '');

  return [
    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.PLAN_LIST)}`,
      async ({ request }) => {
        const originalResponse = await fetch(bypass(request));
        const plans: RawPlan[] = await originalResponse.json();

        // The first plan's whole group (RT + OW variants render as one card) stays out of
        // the campaign: effectivePrice equals price, no discounts.
        const firstGroup = plans[0]?.group;

        // Ordered discounted groups, so every plan in a group shares one duration type.
        const otherGroups = plans.map(plan => plan.group).filter(group => group !== firstGroup);
        const discountedGroups = [...new Set(otherGroups)];

        const augmented = plans.map((plan, index) => {
          const isDiscounted = firstGroup ? plan.group !== firstGroup : index > 0;
          const groupIndex = Math.max(discountedGroups.indexOf(plan.group), 0);
          const publicDuration = PUBLIC_DURATIONS[groupIndex % PUBLIC_DURATIONS.length]!;

          return {
            ...plan,
            effectivePrice: scalePrice(plan.price, isDiscounted ? 1 - PUBLIC_DISCOUNT_RATE : 1),
            discounts: isDiscounted ? toDiscounts(plan.price, publicDuration) : [],
          };
        });

        console.log('[MSW] plans campaign discount fields injected:', augmented.length, 'plans');

        return HttpResponse.json(augmented);
      },
    ),
    http.get(
      `${sanitizedBaseUrl}/${toMSWPattern(BFF_ENDPOINTS.ADDON_LIST)}`,
      async ({ request }) => {
        const originalResponse = await fetch(bypass(request));
        const addons: RawAddon[] = await originalResponse.json();

        // Only the first optional add-on joins the campaign; the rest keep full price.
        const discountedCode = addons.find(addon => !addon.is_included)?.code;

        const augmented = addons.map((addon) => {
          const isDiscounted = addon.code === discountedCode;

          return {
            ...addon,
            effectivePrice: scalePrice(addon.price, isDiscounted ? 1 - PUBLIC_DISCOUNT_RATE : 1),
            discounts: isDiscounted ? toDiscounts(addon.price, PUBLIC_DURATIONS[0]!) : [],
          };
        });

        console.log('[MSW] addons campaign discount fields injected:', augmented.length, 'addons');

        return HttpResponse.json(augmented);
      },
    ),
  ];
}
